import React from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';

const HowItWorks = () => {
  const { language } = useLanguage();

  const steps = [
    { number: "01", title: t(language, 'howItWorksPage.step1'), description: t(language, 'howItWorksPage.step1Desc'), icon: "MessageSquare" },
    { number: "02", title: t(language, 'howItWorksPage.step2'), description: t(language, 'howItWorksPage.step2Desc'), icon: "Phone" },
    { number: "03", title: t(language, 'howItWorksPage.step3'), description: t(language, 'howItWorksPage.step3Desc'), icon: "CreditCard" },
    { number: "04", title: t(language, 'howItWorksPage.step4'), description: t(language, 'howItWorksPage.step4Desc'), icon: "Settings" },
    { number: "05", title: t(language, 'howItWorksPage.step5'), description: t(language, 'howItWorksPage.step5Desc'), icon: "CheckCircle" }
  ];

  return (
    <div className="min-h-screen bg-[#fefdf4]">
      {/* Hero */}
      <section className="bg-[#fefdf4] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">{t(language, 'pages.howItWorksHero')}</h1>
          <p className="text-xl text-gray-600">
            {t(language, 'pages.howItWorksSubtitle')}
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {steps.map((step, index) => {
              const Icon = LucideIcons[step.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div key={step.number} className="relative">
                  <div className={`flex flex-col md:flex-row items-center gap-8 ${isEven ? '' : 'md:flex-row-reverse'}`}>
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 bg-blue-900 text-white rounded-full flex items-center justify-center">
                        <Icon className="w-10 h-10" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`flex-1 ${isEven ? 'md:text-left' : 'md:text-right'}`}>
                      <div className="text-sm font-semibold text-blue-900 mb-2">{step.number}</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-lg text-gray-600">{step.description}</p>
                    </div>

                    {/* Spacer */}
                    <div className="hidden md:block flex-shrink-0 w-20"></div>
                  </div>

                  {/* Connector line */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute left-10 top-20 w-0.5 h-16 bg-gray-200"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#f5f2e8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">{t(language, 'howItWorksPage.importantToKnow')}</h3>
            <p className="text-gray-600">
              {t(language, 'howItWorksPage.importantToKnowDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#fefdf4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t(language, 'howItWorksPage.readyToGetStarted')}</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/start">
              <Button size="lg" className="shine-cta w-full sm:w-auto bg-blue-900 hover:bg-blue-800 text-white px-8">
                {t(language, 'nav.startCta')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
